import { useEffect, useId, useMemo, useRef, useState } from "react";
import { MAX_ZNAKOW_ETYKIETY, type LinkRecenzjiAdmin } from "@shared/recenzjeTypy.js";
import { useZasob } from "../../hooks/useZasob.js";
import { api, ApiError, type SessionListEntry } from "../../lib/api.js";
import { odmien } from "../../lib/polski.js";
import {
  OPCJE_WAZNOSCI,
  czyBezOceny,
  czyRozmowaDoRecenzji,
  dniWaznosci,
  komunikatBleduRecenzji,
  komunikatBleduWyboruRozmow,
  pelnyAdresLinku,
  rozmowyKontUzytkownikowZOdmowy,
  walidujFormularzLinku,
  zdanieOLinku,
  type BledyFormularzaLinku,
  type KluczWaznosci,
} from "../../lib/recenzje.js";
import { IconKopiuj, IconOstrzezenie } from "../icons/index.js";
import { Banner } from "../ui/Banner.js";
import { Button } from "../ui/Button.js";
import { Checkbox } from "../ui/Choice.js";
import { Dialog } from "../ui/Dialog.js";
import { Field, Input } from "../ui/Field.js";
import { Select } from "../ui/Select.js";
import { useToast } from "../ui/Toast.js";
import { StanBledu, StanLadowania, StanPusty } from "../ui/stany.js";
import { useKopiowanie } from "./useKopiowanie.js";

/**
 * Okno "Link do recenzji": tworzy link /r/<token> dla zewnetrznego recenzenta. Uzywane z ekranu analizy ("Wyslij do
 * recenzji", biezaca rozmowa zaznaczona) i z panelu Recenzje ("Nowy link", nic nie zaznaczone).
 *
 * Dwa kroki w jednym oknie (D-1): formularz, potem "Link gotowy". Okno jest montowane tylko wtedy, gdy jest otwarte, wiec
 * kazde otwarcie zaczyna od czystego formularza, a lista rozmow nie jest pobierana, dopoki nikt o nia nie prosi.
 *
 * Walidacja stoi pod polami (F-12), nie w toascie. Blad serwera tez zostaje w oknie, nad przyciskami.
 */

interface Biezaca {
  id: string;
  tytul: string;
  liczbaWypowiedzi: number;
}

interface Props {
  otwarty: boolean;
  onZamknij: () => void;
  /**
   * Rozmowa, z ktorej okno otwarto. Jest zaznaczona na starcie i zawsze na liscie, nawet gdy jest starsza niz to, co
   * zwraca lista analiz.
   */
  biezaca?: Biezaca;
  /** Po utworzeniu linku (panel odswieza liste). */
  onUtworzono?: (link: LinkRecenzjiAdmin) => void;
}

export function DialogLinkuRecenzji(props: Props): React.JSX.Element | null {
  if (!props.otwarty) return null;
  return <TrescDialogu {...props} />;
}

interface PozycjaListy {
  id: string;
  tytul: string;
  liczbaWypowiedzi: number;
  biezaca: boolean;
  /** Rozmowa skonczona, ale bez oceny: pokazana, lecz nie do zaznaczenia (recenzent dostalby slepy zaulek). */
  bezOceny: boolean;
  /**
   * Rozmowa konta uzytkownika: nie trafia do linku przed zgoda uczestnikow. Zwykle mowi to juz lista analiz administratora
   * (zKontaUzytkownika) i pozycja stoi wtedy na koncu, wylaczona, jak rozmowa bez oceny. Gdy lista nie mowi (biezaca
   * rozmowa spoza listy, starszy serwer), wiemy to z odmowy serwera, a pozycja zostaje na swoim miejscu, ale wylaczona.
   */
  kontoUzytkownika: boolean;
}

/** Czy pozycje mozna zaznaczyc i wyslac w linku. */
function doWyboru(p: PozycjaListy): boolean {
  return !p.bezOceny && !p.kontoUzytkownika;
}

/** Ile analiz z listy bierzemy pod uwage w oknie (serwer ogranicza liste do 100 najnowszych). */
const LIMIT_LISTY_ANALIZ = 100;

function opisPozycji(p: PozycjaListy): string {
  const wypowiedzi = `${p.liczbaWypowiedzi} ${odmien(p.liczbaWypowiedzi, "wypowiedź", "wypowiedzi", "wypowiedzi")}`;
  // Powod wylaczenia stoi w tekscie, nie tylko w przezroczystosci wiersza.
  const czesci = [wypowiedzi];
  if (p.biezaca) czesci.push("ta rozmowa");
  if (p.bezOceny) czesci.push("bez oceny");
  if (p.kontoUzytkownika) czesci.push("z konta użytkownika");
  return czesci.join(", ");
}

function TrescDialogu({ onZamknij, biezaca, onUtworzono }: Props): React.JSX.Element {
  const toast = useToast();
  const idBazowe = useId();
  const idRozmow = `${idBazowe}-rozmowy`;
  const idBleduRozmow = `${idBazowe}-rozmowy-blad`;

  const [etykieta, setEtykieta] = useState("");
  const [zaznaczone, setZaznaczone] = useState<readonly string[]>(biezaca ? [biezaca.id] : []);
  const [waznosc, setWaznosc] = useState<KluczWaznosci>("bez");
  const [bledy, setBledy] = useState<BledyFormularzaLinku>({});
  const [bladSerwera, setBladSerwera] = useState<string | null>(null);
  const [zajety, setZajety] = useState(false);
  const [utworzony, setUtworzony] = useState<LinkRecenzjiAdmin | null>(null);
  // Id rozmow kont uzytkownikow, ktore serwer wskazal w odmowie (patrz PozycjaListy.kontoUzytkownika).
  const [kontaUzytkownikow, setKontaUzytkownikow] = useState<ReadonlySet<string>>(() => new Set());

  const refEtykiety = useRef<HTMLInputElement>(null);
  const refPolaLinku = useRef<HTMLInputElement>(null);
  const refRozmow = useRef<HTMLDivElement>(null);
  const { skopiowano, kopiuj } = useKopiowanie();

  const lista = useZasob("rozmowy-do-recenzji", () => api.sessions(LIMIT_LISTY_ANALIZ));

  /**
   * Biezaca na gorze (zawsze na liscie), potem rozmowy z ocena w kolejnosci serwera (od najnowszych), na koncu
   * rozmowy wylaczone: bez oceny i z kont uzytkownikow wskazanych na liscie analiz.
   */
  const pozycje = usePozycjeListy(lista.dane?.sessions, biezaca, kontaUzytkownikow);
  const jestBezOceny = pozycje.some((p) => p.bezOceny);
  const jestZKontaUzytkownika = pozycje.some((p) => p.kontoUzytkownika);

  // Krok 2: fokus na polu z linkiem, zaznaczone, zeby od razu mozna bylo skopiowac. Dialog pamieta wyzwalacz i odda mu
  // fokus przy zamknieciu.
  useEffect(() => {
    if (!utworzony) return;
    refPolaLinku.current?.focus();
    refPolaLinku.current?.select();
  }, [utworzony]);

  function przelacz(id: string, zaznacz: boolean): void {
    setZaznaczone((obecne) => (zaznacz ? (obecne.includes(id) ? obecne : [...obecne, id]) : obecne.filter((x) => x !== id)));
    setBledy((b) => ({ ...b, rozmowy: undefined }));
    setBladSerwera(null);
  }

  async function utworz(): Promise<void> {
    if (zajety) return;
    // Kolejnosc rozmow w linku = kolejnosc na liscie, nie kolejnosc klikania. Rozmowy bez oceny i rozmowy kont
    // uzytkownikow nie wchodza do linku, nawet gdy byly zaznaczone na starcie (biezaca rozmowa) albo przed odmowa serwera.
    const rozmowaIds = pozycje.filter((p) => doWyboru(p) && zaznaczone.includes(p.id)).map((p) => p.id);
    const znalezione = walidujFormularzLinku({ etykieta, rozmowaIds });
    setBledy(znalezione);
    setBladSerwera(null);
    if (znalezione.etykieta) {
      refEtykiety.current?.focus();
      return;
    }
    if (znalezione.rozmowy) {
      refRozmow.current?.querySelector<HTMLElement>("input")?.focus();
      return;
    }

    setZajety(true);
    try {
      const link = await api.recenzjeUtworzLink({ etykieta: etykieta.trim(), rozmowaIds, waznyDni: dniWaznosci(waznosc) });
      setUtworzony(link);
      onUtworzono?.(link);
    } catch (err) {
      const kod = err instanceof ApiError ? err.code : "siec";
      const szczegoly = err instanceof ApiError ? err.szczegoly : {};
      const bladWyboru = komunikatBleduWyboruRozmow(kod, szczegoly);
      const kontUzytkownikow = rozmowyKontUzytkownikowZOdmowy(kod, szczegoly);
      if (bladWyboru) {
        // Serwer odrzucil sam wybor rozmow: komunikat stoi pod lista. Rozmowy kont uzytkownikow wskazane w odmowie
        // wylaczamy na miejscu: lista ich nie oznaczyla, wiec swieza tez by nie oznaczyla. Inaczej (np. ocena zniknela
        // w miedzyczasie) lista pobiera sie od nowa, zeby bylo widac, ktore rozmowy sa juz bez oceny.
        setBledy((b) => ({ ...b, rozmowy: bladWyboru }));
        if (kontUzytkownikow.length > 0) setKontaUzytkownikow((obecne) => new Set([...obecne, ...kontUzytkownikow]));
        else lista.odswiez();
      } else {
        setBladSerwera(komunikatBleduRecenzji(kod));
      }
    } finally {
      setZajety(false);
    }
  }

  const adres = utworzony ? pelnyAdresLinku(utworzony, window.location.origin) : "";

  async function kopiujAdres(): Promise<void> {
    if (!(await kopiuj("nowy", adres))) {
      // Schowek zablokowany: pole z linkiem jest zaznaczone, wiec wystarczy Ctrl+C. Toast bledu, nie ostrzezenia: okno
      // otwiera sie tez ze strony analizy, a tam bursztyn to zloto oceny, czyli "najlepsze". To nieudana operacja, jak
      // kopiowanie w wyniku uzytkownika (Znalezisko.tsx), a czytnik oglasza ja tak samo od razu (role="alert").
      refPolaLinku.current?.select();
      toast.blad("Nie udało się skopiować automatycznie. Link jest zaznaczony, skopiuj go skrótem Ctrl+C.");
    }
  }

  function zobaczJakoRecenzent(): void {
    window.open(adres, "_blank", "noopener");
  }

  // Dopoki lista rozmow nie przyszla, a okno nie ma wlasnej (biezacej) rozmowy, nie ma czego pokazac poza stanem.
  const brakListy = lista.dane === null && !biezaca;

  // Jedno <Dialog> na oba kroki: wyzwalacz, pulapka fokusu i blokada przewijania tla zostaja te same przy przejsciu
  // z formularza do "Link gotowy". Fokus na polu z linkiem ustawia efekt wyzej.
  return (
    <Dialog
      otwarty
      onZamknij={onZamknij}
      tytul={utworzony ? "Link gotowy" : "Link do recenzji"}
      poczatkowyFokusRef={refEtykiety}
      akcje={
        utworzony ? (
          <>
            <Button wariant="secondary" onClick={zobaczJakoRecenzent}>
              Zobacz jako recenzent
            </Button>
            <Button wariant="primary" onClick={onZamknij}>
              Gotowe
            </Button>
          </>
        ) : (
          <>
            <Button wariant="secondary" onClick={onZamknij}>
              Anuluj
            </Button>
            <Button wariant="primary" zajety={zajety} onClick={() => void utworz()}>
              Utwórz link
            </Button>
          </>
        )
      }
    >
      {utworzony ? (
        <>
          <Banner wariant="info" tekst={zdanieOLinku(utworzony.rozmowy.length || zaznaczone.length)} />
          <Field
            etykieta={`Link dla: ${utworzony.etykieta}`}
            id={`${idBazowe}-adres`}
            hint="Link zawsze skopiujesz ponownie w panelu Recenzje. Tam też go unieważnisz."
          >
            {(atrybuty) => (
              <div className="recenzje-pole-linku">
                <input
                  {...atrybuty}
                  ref={refPolaLinku}
                  className="input"
                  readOnly
                  value={adres}
                  onFocus={(e) => e.currentTarget.select()}
                />
                <Button wariant="secondary" onClick={() => void kopiujAdres()}>
                  <IconKopiuj size={18} />
                  {skopiowano === "nowy" ? "Skopiowano" : "Kopiuj"}
                </Button>
              </div>
            )}
          </Field>
        </>
      ) : (
        <>
          <p className="recenzje-dialog__lead">
            Link otworzy wybrane rozmowy z oceną do przejrzenia. Osoba z linkiem nie musi się logować.
          </p>

          <Input
            ref={refEtykiety}
            etykieta="Dla kogo (etykieta)"
            id={`${idBazowe}-etykieta`}
            hint="Tak podpiszemy link w panelu Recenzje. Recenzent zobaczy ją w nagłówku strony."
            blad={bledy.etykieta}
            maxLength={MAX_ZNAKOW_ETYKIETY}
            value={etykieta}
            onChange={(e) => {
              setEtykieta(e.target.value);
              setBledy((b) => ({ ...b, etykieta: undefined }));
              setBladSerwera(null);
            }}
            disabled={zajety}
          />

          <div
            className="field"
            role="group"
            aria-labelledby={idRozmow}
            aria-describedby={bledy.rozmowy ? idBleduRozmow : undefined}
            ref={refRozmow}
          >
            <span className="field-label" id={idRozmow}>
              Rozmowy do przejrzenia
            </span>
            {brakListy && lista.blad !== null ? (
              <StanBledu
                tytul="Nie udało się wczytać rozmów"
                opis={komunikatBleduRecenzji(lista.blad)}
                akcja={
                  <Button wariant="secondary" onClick={lista.odswiez}>
                    Spróbuj ponownie
                  </Button>
                }
              />
            ) : brakListy ? (
              <StanLadowania tekst="Wczytuję rozmowy..." />
            ) : pozycje.length === 0 ? (
              <StanPusty
                tytul="Nie ma jeszcze rozmów z gotową oceną"
                opis="Do recenzji nadają się rozmowy po analizie, dla których uruchomiono ocenę. Wgraj rozmowę, poczekaj na koniec analizy i oceń ją na ekranie analizy."
              />
            ) : (
              <>
                <ul className="choice-list recenzje-rozmowy">
                  {pozycje.map((p) => (
                    <li key={p.id}>
                      <Checkbox
                        nazwa="rozmowa"
                        etykieta={p.tytul}
                        opis={opisPozycji(p)}
                        zaznaczony={doWyboru(p) && zaznaczone.includes(p.id)}
                        wylaczony={zajety || !doWyboru(p)}
                        onZmiana={(z) => przelacz(p.id, z)}
                      />
                    </li>
                  ))}
                </ul>
                {jestBezOceny && (
                  <span className="field-hint">Rozmowę bez oceny najpierw oceń na jej ekranie analizy.</span>
                )}
                {jestZKontaUzytkownika && (
                  <span className="field-hint">
                    Rozmowy z kont użytkowników nie trafiają do recenzji przed zgodą uczestników.
                  </span>
                )}
              </>
            )}
            {/* Okno otwarte z ekranu analizy ma juz biezaca rozmowe, wiec reszta listy dochodzi po cichu. */}
            {!brakListy && lista.dane === null && (
              <span className="field-hint">
                {lista.blad !== null ? "Nie udało się wczytać pozostałych rozmów." : "Wczytuję pozostałe rozmowy..."}
              </span>
            )}
            {bledy.rozmowy && (
              <span className="field-error" id={idBleduRozmow} role="alert">
                <IconOstrzezenie size={14} />
                {bledy.rozmowy}
              </span>
            )}
          </div>

          <Field etykieta="Ważny" id={`${idBazowe}-waznosc`}>
            {(atrybuty) => (
              <Select
                {...atrybuty}
                wartosc={waznosc}
                opcje={OPCJE_WAZNOSCI.map((o) => ({ wartosc: o.wartosc, etykieta: o.etykieta }))}
                onZmiana={setWaznosc}
              />
            )}
          </Field>

          {bladSerwera && (
            <span className="field-error recenzje-dialog__blad" role="alert">
              <IconOstrzezenie size={14} />
              {bladSerwera}
            </span>
          )}
        </>
      )}
    </Dialog>
  );
}

/**
 * Pozycje listy: biezaca rozmowa pierwsza, potem skonczone analizy z wypowiedziami i ocena, na koncu wylaczone: bez
 * oceny i rozmowy kont uzytkownikow wskazane na liscie analiz (zKontaUzytkownika). Biezaca pochodzi z ekranu oceny, wiec
 * uchodzi za oceniana, chyba ze swieza lista mowi wprost, ze oceny nie ma albo ze to rozmowa konta uzytkownika. Brak
 * pola na liscie to "nie wiadomo", nie blokada. Rozmowy kont uzytkownikow znane dopiero z odmowy serwera zostaja na
 * swoim miejscu, wylaczone, zeby lista nie przeskakiwala pod reka.
 */
function usePozycjeListy(
  sesje: readonly SessionListEntry[] | undefined,
  biezaca: Biezaca | undefined,
  kontaUzytkownikow: ReadonlySet<string>,
): PozycjaListy[] {
  return useMemo(() => {
    const naGorze: PozycjaListy[] = [];
    // Na koniec idzie to, co wylaczone z gory: rozmowy bez oceny i rozmowy kont uzytkownikow wedlug listy.
    const naKoncu: PozycjaListy[] = [];
    const naLiscie = sesje?.find((s) => s.id === biezaca?.id);
    if (biezaca) {
      const kontoZListy = naLiscie?.zKontaUzytkownika === true;
      const pozycja: PozycjaListy = {
        id: biezaca.id,
        tytul: biezaca.tytul,
        liczbaWypowiedzi: biezaca.liczbaWypowiedzi,
        biezaca: true,
        bezOceny: naLiscie ? czyBezOceny(naLiscie) : false,
        kontoUzytkownika: kontoZListy || kontaUzytkownikow.has(biezaca.id),
      };
      (pozycja.bezOceny || kontoZListy ? naKoncu : naGorze).push(pozycja);
    }
    for (const s of sesje ?? []) {
      if (s.id === biezaca?.id || !czyRozmowaDoRecenzji(s)) continue;
      const kontoZListy = s.zKontaUzytkownika === true;
      const pozycja: PozycjaListy = {
        id: s.id,
        tytul: s.title,
        liczbaWypowiedzi: s.utteranceCount,
        biezaca: false,
        bezOceny: czyBezOceny(s),
        kontoUzytkownika: kontoZListy || kontaUzytkownikow.has(s.id),
      };
      (pozycja.bezOceny || kontoZListy ? naKoncu : naGorze).push(pozycja);
    }
    return [...naGorze, ...naKoncu];
  }, [sesje, biezaca, kontaUzytkownikow]);
}
